import { isAddress } from 'ethers/lib/utils';
import { parseUnits } from '@ethersproject/units';
import { useContext, useMemo, useState } from 'react';
import {
    INCENTIVE_TOKEN_EQUALS_UNDERLYING_ASSET,
    INCENTIVIZE_SPOOL_PENDING,
    INSUFFICIENT_FUNDS_ERROR,
    SPOOL_INCENTIVIZED,
    TimeConversions,
} from '../../../../../../../../config/constants';
import modalContext from '../../../../../../../../context/modal/modalContext';
import INCENTIVIZE_GUIDE from '../../../../../../../../config/constants/incentivizeGuide.json';
import Button from '../../../../../../../../components/Button';
import IncentiveTokens from '../IncentivizeTokens';
import { SpoolDto } from '../../../../../../../../types/spool-local';
import useTokenDetails from '../../../../../../../../hooks/web3/useTokenDetails';
import appContext from '../../../../../../../../context/app/appContext';
import useIsApproved from '../../../../../../../../hooks/web3/useIsApproved';
import useUserBalance from '../../../../../../../../hooks/web3/useUserBalance';
import { toBigNumber } from '../../../../../../../../web3/utils';
import errorContext from '../../../../../../../../context/error/errorContext';

type Props = {
    spoolDetails: SpoolDto;
};

export type IncentiveTokenType = {
    address: string;
    amount: string;
    rate: string;
};

const AddIncentive: React.FC<Props> = ({ spoolDetails }) => {
    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const { asset } = spoolDetails;

    const { interfaceSDK } = useContext(appContext);

    const { setError } = useContext(errorContext);

    const [incentiveToken, setIncentiveToken] = useState<IncentiveTokenType>({
        address: '',
        amount: '',
        rate: '',
    });

    const tokenDetails = useTokenDetails(
        isAddress(incentiveToken.address) ? incentiveToken.address : undefined
    );

    const userBalance = useUserBalance(incentiveToken.address);

    const isValidToken = useMemo(
        () => tokenDetails.symbol && tokenDetails.decimals,
        [tokenDetails]
    );

    const isValidated = useMemo(
        () =>
            isAddress(incentiveToken.address) &&
            incentiveToken.amount &&
            incentiveToken.rate &&
            isValidToken,
        [incentiveToken, isValidToken]
    );

    const { isApproved } = useIsApproved(
        incentiveToken.address,
        spoolDetails.address,
        incentiveToken.amount
    );

    const handleIncentiveTokenChange = (dataKey: string, data: string) => {
        setIncentiveToken((prev) => ({ ...prev, [dataKey]: data }));
    };

    const incentivizeSpool = async () => {
        try {
            if (!isValidated || !interfaceSDK || !userBalance) return;

            if (asset.id.toLowerCase() === incentiveToken.address.toLowerCase()) {
                openModal('errorModal');
                setModalData((prev) => ({
                    ...prev,
                    message: INCENTIVE_TOKEN_EQUALS_UNDERLYING_ASSET,
                }));
                return;
            }

            if (
                userBalance.lt(
                    toBigNumber(incentiveToken.amount || '0', tokenDetails.decimals)
                )
            ) {
                openModal('errorModal');
                setModalData((prev) => ({
                    ...prev,
                    message: INSUFFICIENT_FUNDS_ERROR,
                }));
                return;
            }

            setModalData((prev) => ({ ...prev, message: INCENTIVIZE_SPOOL_PENDING }));
            openModal('waitingModal');

            const vaultContract = interfaceSDK.get.getVaultContractInstance(
                spoolDetails.address
            );

            const incentivize = await vaultContract.addToken(
                incentiveToken.address,
                +incentiveToken.rate * TimeConversions.SECONDS_PER_DAY,
                parseUnits(incentiveToken.amount, tokenDetails.decimals)
            );

            const receipt = await incentivize.wait();

            setModalData((prev) => ({
                ...prev,
                status: SPOOL_INCENTIVIZED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <>
            <span>Incentivize your Spool with an ERC20 token.</span>
            <div className='incentivize-step-container'>
                {Object.entries(INCENTIVIZE_GUIDE).map(([key, value]) => (
                    <div key={key} className='step-container'>
                        <div className='step'>{key}</div>
                        <p>{value}</p>
                    </div>
                ))}
            </div>
            <IncentiveTokens
                spoolDetails={spoolDetails}
                token={incentiveToken}
                handleIncentiveTokenChange={handleIncentiveTokenChange}
                isValidated={isValidated}
                isValidToken={isValidToken}
            />

            <div className='action-container'>
                <Button disabled={!isValidated || !isApproved} onClick={incentivizeSpool}>
                    Incentivize Spool
                </Button>
            </div>
        </>
    );
};

export default AddIncentive;
